// The version describes measurement semantics, not the user's AI access setting.
export const USAGE_SCHEMA_VERSION = 2;
export const INTERVAL_MS = 30000;
export const IDLE_MS = 60000;
export const PLAYBACK_FRESH_MS = 2500;
const MAX_TICK_GAP_MS = 5000;
const DAY_MS = 86400000;

export function usageRoute(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  const routes = { dashboard: "dashboard", videos: "videos", settings: "settings", onboarding: "onboarding", admin: "admin", reviewer: "reviewer" };
  if (parts[0] === "video" && parts.length === 3) {
    return { screen: "video", videoId: /^[A-Za-z0-9_-]{1,128}$/.test(parts[2]) ? parts[2] : null };
  }
  if (parts[0] === "case-prep") {
    const screen = parts.length === 1 ? "case_prep" : parts[2] === "print" ? "case_prep_print" : parts[2] === "cases" ? "case_prep_case" : "case_prep_day";
    return { screen, videoId: null };
  }
  return { screen: routes[parts[0]] || null, videoId: null };
}

// No React state or network work on progress/interaction events. The injectable
// clocks make hidden time, clock changes, suspension and idle cutoffs testable.
export function createUsageTracker({ now, wallNow, uuid, emit, context, foreground = false }) {
  const sessionId = uuid();
  let sequence = 0;
  let previous = now();
  let previousWall = wallNow();
  let lastInteraction = previous;
  let playbackUntil = -Infinity;
  let currentContext = context;
  let eligible = foreground;
  let pendingLoss = { droppedIntervals: 0, unobservedMs: 0 };
  let interval = null;

  function begin(at) {
    interval = { ...currentContext, schemaVersion: USAGE_SCHEMA_VERSION, eventId: uuid(), sessionId, sequence: sequence++,
      startedAtMs: at, endedAtMs: at, foregroundMs: 0, activeMs: 0, videoWatchMs: 0,
      ...pendingLoss };
    pendingLoss = { droppedIntervals: 0, unobservedMs: 0 };
  }
  function finish() {
    if (!interval) return;
    if (interval.endedAtMs > interval.startedAtMs && (interval.foregroundMs || interval.droppedIntervals || interval.unobservedMs)) emit(interval);
    else {
      pendingLoss.droppedIntervals += interval.droppedIntervals;
      pendingLoss.unobservedMs += interval.unobservedMs;
    }
    interval = null;
  }
  function advance() {
    const time = now();
    const wall = wallNow();
    const elapsed = time - previous;
    const wallElapsed = wall - previousWall;
    if (elapsed < 0 || wallElapsed < 0 || elapsed > MAX_TICK_GAP_MS || Math.abs(elapsed - wallElapsed) > 2000) {
      finish();
      // A suspended tab is not evidence of watching or interacting.
      if (eligible) pendingLoss.unobservedMs = Math.min(7 * DAY_MS, pendingLoss.unobservedMs + Math.max(0, wallElapsed));
      playbackUntil = -Infinity;
      previous = time;
      previousWall = wall;
      return;
    }
    if (!currentContext.screen) { previous = time; previousWall = wall; return; }
    let cursor = previousWall;
    while (cursor < wall) {
      if (!interval) begin(cursor);
      const boundary = Math.min(wall, interval.startedAtMs + INTERVAL_MS, (Math.floor(cursor / DAY_MS) + 1) * DAY_MS);
      const duration = Math.max(0, Math.floor(Math.min(boundary - cursor, elapsed * (boundary - cursor) / Math.max(1, wallElapsed))));
      const start = previous + (cursor - previousWall);
      if (eligible) {
        const watch = currentContext.screen === "video" ? Math.max(0, Math.min(duration, Math.floor(playbackUntil - start))) : 0;
        const interaction = Math.max(0, Math.min(duration, Math.floor(lastInteraction + IDLE_MS - start)));
        interval.foregroundMs += duration;
        interval.activeMs += Math.max(interaction, watch);
        interval.videoWatchMs += watch;
      }
      interval.endedAtMs = boundary;
      cursor = boundary;
      if (boundary === interval.startedAtMs + INTERVAL_MS || boundary % DAY_MS === 0) finish();
    }
    previous = time;
    previousWall = wall;
  }
  return {
    tick: advance,
    flush() { advance(); finish(); },
    setContext(next) {
      if (JSON.stringify(next) === JSON.stringify(currentContext)) return;
      advance(); finish();
      if (next.screen !== currentContext.screen || next.videoId !== currentContext.videoId) { playbackUntil = -Infinity; }
      currentContext = next;
    },
    setForeground(next) {
      if (eligible === next) return;
      advance(); finish(); eligible = next;
      if (next) lastInteraction = now();
      else playbackUntil = -Infinity;
    },
    interact() { advance(); lastInteraction = now(); },
    playback({ playing }) { advance(); playbackUntil = playing ? now() + PLAYBACK_FRESH_MS : -Infinity; },
    recordLoss({ droppedIntervals = 0, unobservedMs = 0 }) {
      pendingLoss.droppedIntervals = Math.min(1000000, pendingLoss.droppedIntervals + droppedIntervals);
      pendingLoss.unobservedMs = Math.min(7 * DAY_MS, pendingLoss.unobservedMs + unobservedMs);
    },
  };
}
